import React, { useCallback, useMemo } from "react";
import { FieldProps } from "react-jsonschema-form";
import { useIntl } from "react-intl";
import styled from "styled-components";
import { arrayMove } from "react-sortable-hoc";
import { SortableSelect } from "../../shared/widgets/SortableSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { messages } from "../messages";

const StyledSelector = styled.div`
  max-width: 758px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const DeleteAll = styled.button<{ visible: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  color: #6c757d;
  cursor: pointer;
  font-size: 13px;
  visibility: ${({ visible }) => (visible ? "visible" : "hidden")};
  &:hover {
    opacity: 0.75;
  }
`;

const OptionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MaterialIcon = styled.span`
  font-size: 16px;
  line-height: 1;
`;

export interface SelectOption {
  value: string;
  icon?: string;
  translations?: { locale: string; name: string }[];
}

interface PropertyMultiSelectProps extends FieldProps {
  options?: SelectOption[];
  placeholder?: string;
  enableSort?: boolean;
}

export function PropertyMultiSelect({
  formData,
  onChange,
  options = [],
  placeholder,
  enableSort = true,
}: PropertyMultiSelectProps) {
  const intl = useIntl();
  const locale = intl.locale.split("-")[0];

  const getLabel = useCallback(
    (option: SelectOption): string =>
      option.translations?.find(t => t.locale === locale)?.name ??
      option.translations?.[0]?.name ??
      option.value,
    [locale]
  );

  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => getLabel(a).localeCompare(getLabel(b), locale)),
    [options, getLabel, locale]
  );

  const selectedOptions = useMemo<SelectOption[]>(() => {
    if (formData && typeof formData === "object" && !Array.isArray(formData)) {
      return Object.values(formData).map(opt => {
        const stored = opt as SelectOption;
        return options.find(o => o.value === stored.value) ?? stored;
      });
    }
    return [];
  }, [formData, options]);

  const toRecord = useCallback(
    (selected: SelectOption[]): Record<string, SelectOption> =>
      selected.reduce((acc, opt) => ({ ...acc, [opt.value]: opt }), {}),
    []
  );

  const handleChange = useCallback(
    (value: SelectOption[] | null) => {
      onChange(toRecord(value ?? []));
    },
    [onChange, toRecord]
  );

  const handleSortEnd = useCallback(
    ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
      const reordered = arrayMove(selectedOptions, oldIndex, newIndex);
      onChange(toRecord(reordered));
    },
    [selectedOptions, onChange, toRecord]
  );

  const handleDeleteAll = useCallback(() => {
    onChange({});
  }, [onChange]);

  const formatOptionLabel = useCallback(
    (option: SelectOption) => {
      const label = getLabel(option);
      return (
        <OptionLabel>
          {option.icon && (
            <MaterialIcon className="material-symbols-outlined">{option.icon}</MaterialIcon>
          )}
          <span>{label}</span>
        </OptionLabel>
      );
    },
    [getLabel]
  );

  const effectivePlaceholder = placeholder ?? intl.formatMessage(messages.amenitiesPlaceholder);
  const noOptionsMessage = useCallback(() => intl.formatMessage(messages.amenitiesNoOptions), [
    intl,
  ]);

  return (
    <StyledSelector>
      <Header>
        <div />
        <DeleteAll type="button" visible={selectedOptions.length > 0} onClick={handleDeleteAll}>
          <FontAwesomeIcon icon={faTrashCan} />
          {intl.formatMessage(messages.amenitiesDeleteAll)}
        </DeleteAll>
      </Header>
      <SortableSelect
        useDragHandle={enableSort}
        axis="xy"
        onSortEnd={enableSort ? handleSortEnd : undefined}
        distance={4}
        options={sortedOptions}
        value={selectedOptions}
        onChange={handleChange}
        closeMenuOnSelect={false}
        placeholder={effectivePlaceholder}
        formatOptionLabel={formatOptionLabel}
        noOptionsMessage={noOptionsMessage}
      />
    </StyledSelector>
  );
}
